import type { DiscountListItemType } from "@/features/cart/types/discount.type";
import { formatPrice } from "@/lib/utils";

const normalizeCode = (code: string) => code.trim().toUpperCase();

// Promotion coupons have no name of their own: the API repeats the code as
// the title. Such a coupon gets a title saying it works only in the app.
const hasOwnTitle = (title: string | null, code: string) =>
  Boolean(title?.trim()) && normalizeCode(title ?? "") !== normalizeCode(code);

// What the code is worth, e.g. "10%" or "60 zł". The API sends amounts as
// strings ("60"); a product discount has no value to show.
export const formatCouponDiscountValue = (
  discount: DiscountListItemType["discount"],
): string | null => {
  const value = Number(discount.value);
  if (!value) {
    return null;
  }
  if (discount.type === "percentage") {
    return `${value}%`;
  }
  if (discount.type === "amount") {
    return formatPrice(value, {
      currency: discount.currency ?? undefined,
    }).replace(",00", "");
  }
  return null;
};

// A typed code the web cannot apply (`web_not_supported`), shown in the coupon
// list with the "go to app" button (MPL-3137). The flag is forced because the
// legacy app-only codes are reported by the API as usable on the web.
export const toMobileOnlyCoupon = (
  coupon: DiscountListItemType,
  fallback: { title: string; description: string | null },
): DiscountListItemType => ({
  ...coupon,
  onlyMobileApp: true,
  title: hasOwnTitle(coupon.title, coupon.code) ? coupon.title : fallback.title,
  // Promotion coupons come without a description; like the other coupons,
  // the line under the title then says what the code is worth.
  description: coupon.description?.trim()
    ? coupon.description
    : (fallback.description ?? coupon.description),
});

// Puts the app-only coupon first, unless the list already shows that code.
export const prependMobileOnlyCoupon = (
  coupons: DiscountListItemType[],
  mobileOnlyCoupon: DiscountListItemType | null,
): DiscountListItemType[] => {
  if (!mobileOnlyCoupon) {
    return coupons;
  }
  const code = normalizeCode(mobileOnlyCoupon.code);
  if (coupons.some((coupon) => normalizeCode(coupon.code) === code)) {
    return coupons;
  }
  return [mobileOnlyCoupon, ...coupons];
};
